import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { DefaultEnvironment, ENVIRONMENT_TOKEN } from '@trovata/app/core/models/config.model';
import { ProfilesApiResponse } from '../../tsm-app/models/tsm-app.model';
import { ReportAppDescriptor, ReportResult } from '../models/report-app.model';

/**
 * Report Apps — REST client for the report-app endpoints exposed by
 * {@code AppDataServlet}. Auth is handled by {@link AuthInterceptor} (the
 * {@code atomApiUrl} allowlist already covers these URLs — Jackson Rule #2).
 * No manual {@code getAuthHeaders} pattern.
 *
 * Endpoint shape (server RP-1..RP-3):
 *   - {@code ?action=descriptor}   → {@link ReportAppDescriptor} (filter pane + format block)
 *   - {@code ?action=refresh}      → {@link ReportAppDescriptor}
 *   - {@code ?action=rebind}       → {@link ReportAppDescriptor} (Path B re-attach)
 *   - {@code ?action=profiles}     → {@link ProfilesApiResponse}
 *   - {@code ?action=runReport}    → {@link ReportResult} (the {header,columns,rows} contract)
 *   - {@code ?action=reportExport} → Blob (XLSX/PDF) / text (SQL), handled by {@code ReportDownloadService}
 *
 * All POSTs carry an {@code idempotencyKey}; callers mint a fresh UUID per
 * request — same convention as {@code FilterAppApiService}.
 */
@Injectable({ providedIn: 'root' })
export class ReportAppApiService {
	private readonly baseUrl: string;

	constructor(
		private readonly http: HttpClient,
		@Inject(ENVIRONMENT_TOKEN) private readonly environment: DefaultEnvironment,
	) {
		this.baseUrl = `${this.environment.atomApiUrl}/api/v1/app-data`;
	}

	/**
	 * Bootstrap the report-app descriptor (filter pane + format block). Server
	 * mints a fresh {@code formId} and caches the report processor in FormCache;
	 * runReport / reportExport reuse the SAME cached processor by formId.
	 */
	bootstrap(appName: string, profileCd: string | undefined, idempotencyKey: string): Observable<ReportAppDescriptor> {
		let params: HttpParams = new HttpParams().set('action', 'descriptor');
		params = params.set('app', appName);
		if (profileCd) {
			params = params.set('profile', profileCd);
		}
		return this.http.post<ReportAppDescriptor>(
			`${this.baseUrl}?${params.toString()}`,
			{ idempotencyKey },
		);
	}

	/**
	 * Apply changed filter fields server-side (driving the observer chain) and
	 * re-project the descriptor. 410 {@code FORM_EXPIRED} fires on cache miss.
	 */
	refresh(
		formId: string,
		changedFields: Readonly<Record<string, string | number | boolean | null>>,
		idempotencyKey: string,
	): Observable<ReportAppDescriptor> {
		return this.http.post<ReportAppDescriptor>(
			`${this.baseUrl}?action=refresh`,
			{ idempotencyKey, formId, changedFields },
		);
	}

	/**
	 * Run the report — server runs the engine pass and projects the
	 * {@code {header, columns, rows}} JSON contract. {@code changedFields} lets
	 * the caller push any last-moment filter edits in the same call (mirrors the
	 * server's {@code handleRunReport} optional changedFields).
	 */
	runReport(
		appName: string,
		formId: string,
		changedFields: Readonly<Record<string, string | number | boolean | null>> | undefined,
		idempotencyKey: string,
	): Observable<ReportResult> {
		const url: string = `${this.baseUrl}?action=runReport&app=${encodeURIComponent(appName)}`;
		const body: Record<string, unknown> = { idempotencyKey, formId };
		if (changedFields && Object.keys(changedFields).length > 0) {
			body['changedFields'] = changedFields;
		}
		return this.http.post<ReportResult>(url, body);
	}

	/**
	 * Fetch the available-profile list for this report. Same response shape as
	 * TSM/filter (AppDataServlet routes all three through {@code handleProfiles}).
	 */
	getProfiles(appName: string): Observable<ProfilesApiResponse> {
		const params: HttpParams = new HttpParams().set('app', appName).set('action', 'profiles');
		return this.http.get<ProfilesApiResponse>(this.baseUrl, { params });
	}

	/**
	 * Path B re-attach. Server returns the same descriptor envelope a fresh
	 * {@link #bootstrap} would emit, read from the cached BAP — the run result
	 * survives verbatim. 410 on cache miss; orchestrator falls back to bootstrap.
	 */
	rebind(formId: string, idempotencyKey: string): Observable<ReportAppDescriptor> {
		return this.http.post<ReportAppDescriptor>(
			`${this.baseUrl}?action=rebind`,
			{ idempotencyKey, formId },
		);
	}
}
